import { Injectable } from '@angular/core';
import { firstValueFrom, map } from 'rxjs';

import { WorkoutApi } from '../../api/workout.api';
import { timeDelay } from '../../helpers/time.helpers';
import { BrowseFilter } from '../../models/filter';
import { BrowseSorting } from '../../models/sorting';
import { Sport } from '../../models/sport';
import { Store } from '../../../../shared/store';
import { WorkoutState } from './workout.state';
import { Workout } from '@kinomap-dev/ng-ui-lib';

const workoutState: WorkoutState = new WorkoutState();

@Injectable({ providedIn: 'root' })
export class WorkoutStore extends Store<WorkoutState> {
  workouts$ = this.state$.pipe(map((s) => s.workouts));
  isLoadingWorkouts$ = this.state$.pipe(map((s) => s.isLoadingWorkouts));
  areWorkoutsComplete$ = this.state$.pipe(map((s) => s.areWorkoutsComplete));

  constructor(private workoutApi: WorkoutApi) {
    super(workoutState);
  }

  // SET WORKOUTS

  setWorkouts(nextWorkouts: Workout[]): void {
    this.setState({ ...this.state, workouts: nextWorkouts });
  }

  setIsLoadingWorkouts(nextIsLoadingWorkouts: boolean): void {
    this.setState({ ...this.state, isLoadingWorkouts: nextIsLoadingWorkouts });
  }

  setAreWorkoutsComplete(nextAreWorkoutsComplete: boolean): void {
    this.setState({
      ...this.state,
      areWorkoutsComplete: nextAreWorkoutsComplete,
    });
  }

  // GET WORKOUTS FROM THE API

  async getWorkouts(
    {
      newSelectedSport,
      newFilters,
      newSorting,
      nextPage,
    }: {
      newSelectedSport?: Sport;
      newFilters?: BrowseFilter[];
      newSorting?: BrowseSorting;
      nextPage: boolean;
    } = { nextPage: false }
  ): Promise<void> {
    if (nextPage && this.state.areWorkoutsComplete) {
      return;
    }

    let goOn: boolean = !this.state.isLoadingWorkouts;
    while (!goOn) {
      await timeDelay(100);
      goOn = !this.state.isLoadingWorkouts;
    }

    if (!nextPage) {
      this.setWorkouts([]);
      this.setAreWorkoutsComplete(false);
    }

    this.setIsLoadingWorkouts(true);

    try {
      const fetchedWorkouts: Workout[] = await firstValueFrom(
        this.workoutApi.getWorkouts$(
          newSelectedSport,
          newFilters,
          newSorting,
          nextPage
        )
      );
      const newWorkouts: Workout[] =
        this.getNewWorkoutsWithoutDuplicate(fetchedWorkouts);
      this.setWorkouts(newWorkouts);

      this.setAreWorkoutsComplete(this.workoutApi.isLastPageLoaded);
    } catch (err) {
      this.setAreWorkoutsComplete(true);
    } finally {
      this.setIsLoadingWorkouts(false);
    }
  }

  private getNewWorkoutsWithoutDuplicate(
    fetchedWorkouts: Workout[]
  ): Workout[] {
    const newWorkoutsWithDuplicates: Workout[] = [
      ...this.state.workouts,
      ...fetchedWorkouts,
    ];
    const obj: {} = {};
    newWorkoutsWithDuplicates.forEach(
      (content: Workout) => (obj[content.id] = content)
    );
    return [
      ...new Set(
        newWorkoutsWithDuplicates.map((workout: Workout) => workout.id)
      ),
    ].map((id: number) => obj[id]);
  }
}
